import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Head } from "./shared";
import { Loader2, RefreshCw, CheckCircle2, XCircle, ExternalLink, Search } from "lucide-react";

export function SeoCenter() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/seo/audit").then((r) => setReport(r.data)).catch(() => setReport(null)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <Head title="SEO Center" desc="On-site SEO validation — titles, descriptions, canonical, schema, sitemap & robots across all public pages and designs." />

      <div className="flex items-center justify-between mb-6">
        <button onClick={load} data-testid="seo-refresh" className="inline-flex items-center gap-2 text-sm border border-white/20 text-gray-200 px-4 py-2 hover:bg-white/5">
          <RefreshCw size={14} /> Re-run audit
        </button>
        {report && (
          <a href={report.counts?.sitemap_url} target="_blank" rel="noreferrer" data-testid="seo-sitemap-link" className="inline-flex items-center gap-2 text-sm text-gold hover:underline">
            View sitemap.xml <ExternalLink size={13} />
          </a>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gold" size={30} /></div>
      ) : !report ? (
        <div className="text-gray-400 py-20 text-center">Could not load the SEO audit.</div>
      ) : (
        <div className="space-y-6" data-testid="seo-report">
          {/* Score */}
          <div className="flex flex-wrap items-center gap-6 border border-gold/25 bg-gold/5 p-6">
            <div className="text-center">
              <div className="font-heading text-5xl text-gold" data-testid="seo-score">{report.score.pct}%</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide mt-1">SEO Score</div>
            </div>
            <div className="text-sm text-gray-300">
              <div><strong className="text-white">{report.score.passed}/{report.score.total}</strong> checks passing</div>
              <div className="text-gray-400 mt-1">
                {report.counts.static_pages} pages · {report.counts.solution_pages} solutions · {report.counts.designs} designs
                · <strong className="text-gray-200">{report.counts.total_indexable}</strong> indexable URLs
              </div>
            </div>
          </div>

          {/* Checklist */}
          <div className="border border-white/10 divide-y divide-white/10" data-testid="seo-checklist">
            {report.checks.map((c) => (
              <div key={c.key} data-testid={`seo-check-${c.key}`} className="flex items-start gap-3 px-5 py-4">
                {c.status === "pass"
                  ? <CheckCircle2 size={18} className="text-green-400 shrink-0 mt-0.5" />
                  : <XCircle size={18} className="text-red-400 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{c.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{c.detail}</div>
                  {c.items && c.items.length > 0 && (
                    <ul className="mt-2 space-y-0.5">
                      {c.items.slice(0, 12).map((it, i) => (
                        <li key={i} className="text-[11px] text-amber-300/90 font-mono truncate">{String(it)}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wide font-semibold shrink-0 ${c.status === "pass" ? "text-green-400" : "text-red-400"}`}>{c.status}</span>
              </div>
            ))}
          </div>

          <p className="text-xs text-gray-500 flex items-center gap-2">
            <Search size={13} /> Submit the sitemap in Google Search Console &amp; Bing Webmaster Tools. Indexed-status and AI recommendations are planned for a future release.
          </p>
        </div>
      )}
    </div>
  );
}
